/**
 * Per-customer QR profiles: which code format a customer mints and which envelope its labels print.
 *
 * A customer with external dependencies on today's labels (LADOTD, RIDOT) runs a legacy profile, so
 * nothing they print or generate changes until they move. Everyone else gets the Standard profile.
 */

/**
 * @typedef {Object} QRProfile
 * @property {string} Name
 * @property {import('./Headlight-QRCode-Minter.js').CodeFormat} CodeFormat
 * @property {import('./Headlight-QRCode-Codec.js').Envelope} Envelope
 * @property {import('./Headlight-QRCode-Minter.js').SiteManagerOptions} [SiteManager]
 */

/** @type {Record<string, QRProfile>} */
const PROFILES =
{
	'Standard':
	{
		Name: 'Standard',
		CodeFormat: 'Standard',
		Envelope: 'Compact',
	},
	'Legacy-Classic':
	{
		Name: 'Legacy-Classic',
		CodeFormat: 'Classic',
		Envelope: 'LegacyLinks',
	},
	'Legacy-SiteManager':
	{
		Name: 'Legacy-SiteManager',
		CodeFormat: 'SiteManager',
		Envelope: 'LegacyLinks',
		SiteManager: { MaxPrefixLength: 9, HourFormat: '12' },
	},
};

const DEFAULT_PROFILE = 'Standard';

/**
 * Customers whose own systems read today's codes. This is only their default: an explicit `QRCodes`
 * profile still wins. It covers an unconfigured customer, or settings that couldn't be read, so these
 * customers never fall through to Standard. Must agree with the QRCodes/Profile records written by
 * `tools/scripts/qrcode_profiles` on every environment.
 *
 * @type {Record<number, string>}
 */
const LEGACY_CUSTOMER_PROFILES =
{
	6: 'Legacy-Classic',
	10: 'Legacy-SiteManager',
};

/** The value `Customer.CustomProperties.CustomSampleGUID` takes for SiteManager-format customers. */
const SITEMANAGER_CUSTOM_SAMPLE_GUID = 'SiteManagerSampleGUID';

/**
 * @param {number|string} [pIDCustomer]
 * @return {string} the profile a customer gets when nothing is configured
 */
function getDefaultProfileName(pIDCustomer)
{
	return LEGACY_CUSTOMER_PROFILES[Number(pIDCustomer)] || DEFAULT_PROFILE;
}

/**
 * Resolve a customer's effective profile.
 *
 * Precedence: an explicit `Profile` name in the customer's QR configuration, then the long-standing
 * `CustomSampleGUID` customer property, then the customer's default (see getDefaultProfileName). Field overrides in the configuration
 * (`CodeFormat`, `Envelope`, `SiteManager`) apply on top of the chosen profile.
 *
 * The compact label envelope is only used when the configuration sets `AllowCompact: true`. Older
 * readers (in-app scanners, smartforms, form mixins, the iPad app) can't read it yet, so it stays
 * off until they are updated; a profile asking for Compact prints LegacyLinks meanwhile.
 *
 * @param {Object} [pConfiguration] - The customer's `QRCodes` configuration, e.g. `{ Profile: 'Legacy-Classic' }`.
 * @param {Object} [pCustomProperties] - The customer's `CustomProperties`.
 * @param {Object} [pOptions]
 * @param {number|string} [pOptions.IDCustomer] - Picks the default for customers with nothing configured.
 * @param {string} [pOptions.DefaultProfile] - Overrides that default.
 * @return {QRProfile & { Configured: boolean, SiteManagerConfigured: boolean }}
 */
function resolveProfile(pConfiguration, pCustomProperties, pOptions)
{
	const tmpConfiguration = pConfiguration || {};
	const tmpProperties = pCustomProperties || {};
	const tmpOptions = pOptions || {};
	const tmpDefault = (tmpOptions.DefaultProfile && PROFILES[tmpOptions.DefaultProfile]) ? tmpOptions.DefaultProfile : getDefaultProfileName(tmpOptions.IDCustomer);
	let tmpBaseName = tmpDefault;
	let tmpConfigured = false;
	if (tmpConfiguration.Profile && PROFILES[tmpConfiguration.Profile])
	{
		tmpBaseName = tmpConfiguration.Profile;
		tmpConfigured = true;
	}
	else if (tmpProperties.CustomSampleGUID === SITEMANAGER_CUSTOM_SAMPLE_GUID)
	{
		tmpBaseName = 'Legacy-SiteManager';
		tmpConfigured = true;
	}
	const tmpBase = PROFILES[tmpBaseName];
	const tmpProfile = Object.assign({}, tmpBase, { Configured: tmpConfigured, SiteManagerConfigured: !!tmpConfiguration.SiteManager });
	if (tmpConfiguration.CodeFormat)
	{
		tmpProfile.CodeFormat = tmpConfiguration.CodeFormat;
	}
	if (tmpConfiguration.Envelope)
	{
		tmpProfile.Envelope = tmpConfiguration.Envelope;
	}
	if (tmpProfile.Envelope === 'Compact' && tmpConfiguration.AllowCompact !== true)
	{
		tmpProfile.Envelope = 'LegacyLinks';
	}
	if (tmpBase.SiteManager || tmpConfiguration.SiteManager)
	{
		tmpProfile.SiteManager = Object.assign({}, tmpBase.SiteManager || {}, tmpConfiguration.SiteManager || {});
	}
	return tmpProfile;
}

module.exports =
{
	PROFILES,
	DEFAULT_PROFILE,
	LEGACY_CUSTOMER_PROFILES,
	SITEMANAGER_CUSTOM_SAMPLE_GUID,
	getDefaultProfileName,
	resolveProfile,
};
